import React, { useState, useEffect } from 'react';
import { SortableHandle } from 'react-sortable-hoc';

import { Spin } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useAddPlaylistToPackage, useDeletePlaylistFromPackage } from '@hooks/API/Packages/UsePackages';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import { BookSelectorDrawer } from '@components/BookSelectorForPackages';

import ModelDrawer from '@components/ContactDetails/ModelDetails/Drawer/ModelDrawer';
import FlexContainer from '@components/Gui/FlexContainer';
import { NwButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import GroupModal from '@components/PackageDrawer/PackageGroupModals/GroupModal';
import NoteModal from '@components/PackageDrawer/PackageModelCard/NoteModal';
import PackageModelPlaylists from '@components/PackageDrawer/PackageModelCard/PackageModelPlaylists';
import AgendaDrawer from '@components/Schedule/Agenda/AgendaDrawer';

import { usePackageContext } from '../PackageContext';
import AddMediaDrawer from './AddMediaDrawer/AddMediaDrawer';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const DragContainer = styled.div`
    display: flex;
    flex-direction: row;
    padding: 10px 24px;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    .package-model-draggable-handler {
        margin: auto 1rem auto 0;
        font-size: 1rem;
    }
    h1 {
        margin: auto 0;
        font-size: 1.1rem;
    }
`;

const PackageModelCard = ({ isSort, loading, model, readOnly, smallView, selectedModels, setSelectedModels }) => {
    const [isOpenNote, setIsOpenNoteModal] = useState(false);
    const [isOpenGroupModal, setIsOpenGroupModal] = useState(false);
    const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
    const [isAgendaDrawer, setIsAgendaDrawer] = useState(false);
    const [playlists, setPlaylists] = useState([]);
    const [isOpenPreview, setIsOpenPreview] = useState(false);
    const [showBooks, setShowBooks] = useState(false);
    const [isMediaDrawer, setIsMediaDrawer] = useState(false);
    const breakpoints = useNwBreakPoints();
    const { mutateAsync: onAddPlaylistToPackage } = useAddPlaylistToPackage();
    const { mutateAsync: onDeletePlaylistFromPackage } = useDeletePlaylistFromPackage();

    const packageContextValue = usePackageContext();
    const { groupedPackage, afterUpdatePackage } = packageContextValue;
    const { ID: packageId, GroupNames } = groupedPackage;

    const layoutContext = useLayoutContext();
    const { isMobile } = layoutContext;

    useEffect(() => {
        if (model) {
            setPlaylists(model.Playlists);
        }
    }, [model]);

    const handleUpdatePlaylistOrder = (playlists) => {
        setPlaylists(playlists);
    };

    const handleOpenNote = () => {
        setIsOpenNoteModal(true);
    };

    const handleCloseNote = (isUpdate, note) => {
        if (isUpdate) {
            afterUpdatePackage();
        }
        setIsOpenNoteModal(false);
    };

    const handleCloseAgendaDrawer = () => {
        setIsAgendaDrawer(false);
    };

    const handleOpenGroupModal = () => {
        setIsOpenGroupModal(true);
    };

    const handleCloseGroupModal = () => {
        setIsOpenGroupModal(false);
    };

    const handleViewModel = () => {
        setIsOpenPreview(true);
    };

    const handleClosePreview = () => {
        setIsOpenPreview(false);
    };

    const handleAddBook = async (book) => {
        const data = {
            packageID: packageId,
            playlistID: book.ID,
            packageGroup: model.Group,
        };
        try {
            await onAddPlaylistToPackage({
                data,
                isActivePackage: true,
            });
        } catch (error) {
            console.error('Add Playlist To Package Error => ', error);
        }
    };

    const handleDeleteBook = async (book) => {
        const playlist = model.Playlists.find((playlist) => playlist.PlaylistID === book.ID);
        try {
            await onDeletePlaylistFromPackage({
                packageID: packageId,
                packagePlaylistId: playlist.ID,
                isActivePackage: true,
            });
            //afterUpdatePackage()
        } catch (error) {
            console.error('Delete Playlist To Package Error => ', error);
        }
    };

    const handleAddBooks = () => {
        setShowBooks(true);
    };

    const handleCloseBooksDrawer = () => {
        setShowBooks(false);
    };

    const handleAddMedia = () => {
        setIsMediaDrawer(true);
    };

    const handleCloseAddMediaDrawer = () => {
        setIsMediaDrawer(false);
    };

    const removeModel = async () => {
        await Axios.delete(`/packagemodels/delete?ModelIDs=${model.ID}`);
        setSelectedModels([]);
        afterUpdatePackage();
    };

    const ExtraContent = () => {
        if (isMobile) {
            const modelactions = [
                {
                    // Niente icona: il pulsante pinned resta compatto e sta sulla stessa riga del nome modella.
                    name: 'add books',
                    key: 'addbooks',
                    pinned: true,
                    action: () => handleAddBooks(),
                },
                {
                    name: 'add media',
                    key: 'addmedia',
                    pinned: false,
                    icon: byPrefixAndName.fasl['circle-plus'],
                    action: () => handleAddMedia(),
                },
                {
                    name: 'edit note',
                    key: 'editnote',
                    pinned: false,
                    icon: byPrefixAndName.fasl['message-pen'],
                    action: () => handleOpenNote(),
                },
                {
                    name: 'change group',
                    key: 'changegroup',
                    pinned: false,
                    icon: byPrefixAndName.fasl['shuffle'],
                    action: () => handleOpenGroupModal(),
                },
                {
                    name: 'model details',
                    key: 'modeldetails',
                    pinned: false,
                    icon: byPrefixAndName.fasl['circle-plus'],
                    action: () => handleViewModel(),
                },
                {
                    name: 'remove model',
                    key: 'removemodel',
                    pinned: false,
                    icon: byPrefixAndName.fasl['user-xmark'],
                    action: () => removeModel(),
                },
            ];
            return <NwDropDownMenu primary right actions={modelactions} />;
        }

        return (
            <FlexContainer wrap="wrap">
                {GroupNames && !readOnly && (
                    <NwButton
                        ghost
                        icon={byPrefixAndName.fasl['shuffle']}
                        label={breakpoints > 2 ? 'change group' : 'group'}
                        primary
                        lowercase
                        onClick={handleOpenGroupModal}
                    />
                )}
                <NwButton
                    ghost
                    icon={byPrefixAndName.fasl['user-xmark']}
                    label={breakpoints > 2 ? 'remove model' : 'remove'}
                    primary
                    lowercase
                    onClick={removeModel}
                />
            </FlexContainer>
        );
    };

    const DragHandle = SortableHandle(() => <NwIcon icon={byPrefixAndName.fasl['sort-alt']} />);

    if (isSort)
        return (
            <DragContainer>
                <div className="package-model-draggable-handler">
                    <DragHandle />
                </div>
                <h1>{getContactName(model.ModelID)}</h1>
            </DragContainer>
        );

    const modelSelectionProps = {
        onSelect: () => {
            setSelectedModels([...selectedModels, model.ID]);
        },
        onDeselect: () => {
            setSelectedModels(selectedModels.filter((id) => id !== model.ID));
        },
        isSelected: selectedModels.includes(model.ID),
    };

    return (
        <Spin spinning={loading || isUpdatingOrder}>
            <NwCard
                selection={!isMobile ? modelSelectionProps : false}
                title={
                    <div className="package-drawer-model-card-title">
                        <h2>{getContactName(model.ModelID)}</h2>
                        {!isMobile && (
                            <NwButton
                                ghost
                                icon={byPrefixAndName.fasl['sidebar-flip']}
                                primary
                                lowercase
                                onClick={handleViewModel}
                            />
                        )}
                    </div>
                }
                extra={<ExtraContent />}
            >
                {loading ? (
                    <></>
                ) : (
                    <div className="package-drawer-model-card-content">
                        <div className="package-drawer-model-card-left">
                            {model.Note && <p className="package-drawer-model-note">{model.Note}</p>}

                            <PackageModelPlaylists
                                contactID={model.ModelID}
                                onSetIsUpdatingOrder={setIsUpdatingOrder}
                                onUpdatePlaylistOrder={handleUpdatePlaylistOrder}
                                packageModelId={model.ID}
                                playlists={playlists}
                                readOnly={readOnly}
                                smallView={smallView}
                            />
                        </div>
                        {!isMobile && (
                            <div className="package-drawer-model-card-right">
                                <div className="package-drawer-model-card-actions-holder">
                                    {model && !readOnly && (
                                        <NwButton
                                            ghost
                                            icon={byPrefixAndName.fasl['message-pen']}
                                            label={breakpoints > 2 ? 'edit note' : 'note'}
                                            primary
                                            lowercase
                                            onClick={handleOpenNote}
                                        />
                                    )}
                                    <NwButton
                                        ghost
                                        icon={byPrefixAndName.fasl['circle-plus']}
                                        label={breakpoints > 2 ? 'add books' : 'books'}
                                        primary
                                        lowercase
                                        onClick={handleAddBooks}
                                    />
                                    <NwButton
                                        ghost
                                        icon={byPrefixAndName.fasl['circle-plus']}
                                        label={breakpoints > 2 ? 'add media' : 'media'}
                                        primary
                                        lowercase
                                        onClick={handleAddMedia}
                                        disabled={
                                            playlists.findIndex((item) => item.Type.toLowerCase() === 'media') >= 0
                                                ? true
                                                : false
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </NwCard>
            {isOpenNote && <NoteModal packageModel={model} isShowModal={isOpenNote} onClose={handleCloseNote} />}
            {isOpenGroupModal && (
                <GroupModal
                    packageModelIDs={[model.ID]}
                    show={isOpenGroupModal}
                    onClose={handleCloseGroupModal}
                    currentGroup={model.Group}
                    clearSelected={() => setSelectedModels([])}
                />
            )}
            {showBooks && (
                <BookSelectorDrawer
                    contactID={model.ModelID}
                    addedPlaylists={model.Playlists.map((playlist) => playlist.PlaylistID)}
                    packageGroup={model.Group}
                    packageName={groupedPackage.Name}
                    onAddBook={handleAddBook}
                    onRemoveBook={handleDeleteBook}
                    onClose={handleCloseBooksDrawer}
                />
            )}
            {isAgendaDrawer && <AgendaDrawer modelId={model.ModelID} onClose={handleCloseAgendaDrawer} />}
            {isOpenPreview && <ModelDrawer modelID={model.ModelID} onClose={handleClosePreview} />}
            {isMediaDrawer && (
                <AddMediaDrawer
                    onClose={handleCloseAddMediaDrawer}
                    modelId={model.ModelID}
                    packageId={packageId}
                    packageGroup={model.Group}
                />
            )}
        </Spin>
    );
};

export default PackageModelCard;
